import { blue, grey, purple, yellow } from '@mui/material/colors';
import { alpha } from '@mui/material/styles';
import Color from 'color';
import cometeColors from './cometeColors';

function createGradient(color1, color2) {
  return `linear-gradient(to bottom, ${color1}, ${color2})`;
}

// SETUP COLORS
const GREY = {
  0: '#FFFFFF',
  100: '#f9fafb',
  200: '#f4f6f8',
  300: '#dfe3e8',
  400: '#c4cdd5',
  500: '#919eab',
  600: '#637381',
  700: '#454f5b',
  800: '#212b36',
  900: '#161c24',
  500_8: alpha('#919EAB', 0.08),
  500_12: alpha('#919EAB', 0.12),
  500_16: alpha('#919EAB', 0.16),
  500_24: alpha('#919EAB', 0.24),
  500_32: alpha('#919EAB', 0.32),
  500_48: alpha('#919EAB', 0.48),
  500_56: alpha('#919EAB', 0.56),
  500_80: alpha('#919EAB', 0.8)
};
const PRIMARY = {
  lightest: '#9ec3f0',
  lighter: '#2A73CE',
  light: '#2462AE',
  main: cometeColors.main,
  dark: '#12325A',
  darker: '#0C213A',
  darkest: '#020d1a',
  contrastText: '#fff'
};
const SECONDARY = {
  lightest: '#ffedc8',
  lighter: '#FFDA8F',
  light: '#FFC752',
  main: '#FFAC00',
  dark: '#AD7500',
  darker: '#704C00',
  darkest: '#704C00',
  contrastText: '#fff'
};
const INFO = {
  lightest: '#ecfaff',
  lighter: '#d0f2ff',
  light: '#74caff',
  main: '#1890ff',
  dark: '#0c53b7',
  darker: '#042a7c',
  darkest: '#041948',
  contrastText: '#fff'
};
const SUCCESS = {
  lightest: '#f7ffee',
  lighter: '#e9fcd4',
  light: '#aaf27f',
  main: '#54d62c',
  dark: '#229a16',
  darker: '#08660d',
  darkest: '#043506',
  contrastText: GREY[800]
};
const WARNING = {
  lightest: '#fffbea',
  lighter: '#fff7cd',
  light: '#ffe16a',
  main: '#ffc107',
  dark: '#b78103',
  darker: '#7a4f01',
  darkest: '#342201',
  contrastText: GREY[800]
};
const ERROR = {
  lightest: '#fff3ec',
  lighter: '#ffe7d9',
  light: '#ffa48d',
  main: '#ff4842',
  dark: '#b72136',
  darker: '#7a0c2e',
  darkest: '#300412',
  contrastText: '#fff'
};
const GRADIENTS = {
  primary: createGradient(PRIMARY.light, PRIMARY.main),
  info: createGradient(INFO.light, INFO.main),
  success: createGradient(SUCCESS.light, SUCCESS.main),
  warning: createGradient(WARNING.light, WARNING.main),
  error: createGradient(ERROR.light, ERROR.main)
};
const CHART_COLORS = {
  violet: ['#826AF9', '#9E86FF', '#D0AEFF', '#F7D2FF'],
  blue: ['#2D99FF', '#83CFFF', '#A5F3FF', '#CCFAFF'],
  green: ['#2CD9C5', '#60F1C8', '#A4F7CC', '#C0F2DC'],
  yellow: ['#FFE700', '#FFEF5A', '#FFF7AE', '#FFF3D6'],
  red: ['#FF6C40', '#FF8F6D', '#FFBD98', '#FFF2D4']
};
const COMMON = {
  common: {
    black: '#000',
    white: '#fff'
  },
  comete: { ...cometeColors },
  primary: { ...PRIMARY },
  secondary: { ...SECONDARY },
  info: { ...INFO },
  success: { ...SUCCESS },
  warning: { ...WARNING },
  error: { ...ERROR },
  grey: GREY,
  gradients: GRADIENTS,
  chart: CHART_COLORS,
  divider: '#EAEDEE', // TODO: plug this value in comete design tokens and replace it by a border token
  borderNeutral: '#D8DEDF', // porcelain 200
  borderFaded: '#EAEDEE', // porcelain 100
  action: {
    hover: GREY[500_8],
    selected: GREY[500_16],
    disabled: GREY[500_80],
    disabledBackground: GREY[500_24],
    focus: GREY[500_24],
    hoverOpacity: 0.08,
    disabledOpacity: 0.48
  }
};
const BACKGROUND = {
  light: {
    main: '#fbfcfe', // fix pour les variables passées en constantes (ex : "primary", voir Toolbar clocking)
    top: '#fbfcfe',
    bottom: '#f9fafc'
  },
  dark: {
    main: '#252A2C', // fix pour les variables passées en constantes (ex : "primary", voir Toolbar clocking)
    top: '#252A2C', // AC : à mettre à jour lorsque le thème dark sera restauré
    bottom: '#252A2C' // AC : à mettre à jour lorsque le thème dark sera restauré
  }
};

const palette = {
  light: {
    ...COMMON,
    action: {
      active: GREY[600],
      ...COMMON.action
    },
    appBackground: {
      ...BACKGROUND.light,
      gradient: createGradient(BACKGROUND.light.top, BACKGROUND.light.bottom)
    },
    background: {
      paper: '#fff',
      default: '#fff',
      neutral: GREY[200],
      squircle: '#ebecf8'
    },
    text: {
      primary: GREY[800],
      secondary: GREY[600],
      disabled: GREY[500]
    },
    weekend: {
      background: GREY[100],
      hover: GREY[200],
      active: GREY[300]
    },
    holiday: {
      background: 'rgba(255,0,0,0.1)',
      hover: 'rgba(255,0,0,0.2)',
      active: 'rgba(255,0,0,0.3)'
    },
    shift: {
      day: {
        background: Color(yellow[50]).lighten(0.035).hex(),
        hover: Color(yellow[50]).darken(0.015).hex(),
        active: yellow[50],
        border: yellow[400],
        color: '#262303'
      },
      night: {
        background: Color(blue[50]).lighten(0.035).hex(),
        hover: Color(blue[50]).darken(0.015).hex(),
        active: blue[50],
        border: blue[100],
        color: '#0e1244'
      }
    },
    unavailability: {
      background: Color(purple[50]).lighten(0.035).hex(),
      hover: Color(purple[50]).darken(0.015).hex(),
      active: purple[50],
      border: purple[100],
      color: purple[900]
    }
  },
  dark: {
    ...COMMON,
    appBackground: {
      ...BACKGROUND.dark,
      gradient: createGradient(BACKGROUND.dark.top, BACKGROUND.dark.bottom)
    },
    action: {
      active: GREY[500],
      ...COMMON.action
    },
    background: {
      paper: '#1d222a',
      default: GREY[900],
      neutral: GREY[500_16],
      squircle: '#ebecf8' // TODO AC : à mettre à jour lorsque le thème dark sera restauré
    },
    text: {
      primary: '#fff',
      secondary: GREY[500],
      disabled: GREY[600]
    },
    weekend: {
      background: GREY[500],
      hover: GREY[600],
      active: GREY[700]
    },
    holiday: {
      background: 'rgba(255,0,0,0.7)',
      hover: 'rgba(255,0,0,0.8)',
      active: 'rgba(255,0,0,0.9)'
    },
    shift: {
      day: {
        background: Color(yellow[800]).lighten(0.035).hex(),
        hover: Color(yellow[800]).darken(0.015).hex(),
        active: yellow[800],
        border: yellow[800],
        color: grey[800]
      },
      night: {
        background: Color(blue[800]).lighten(0.035).hex(),
        hover: Color(blue[800]).darken(0.015).hex(),
        active: blue[800],
        border: blue[800],
        color: blue[50]
      }
    },
    unavailability: {
      background: Color(purple[700]).lighten(0.035).hex(),
      hover: Color(purple[700]).darken(0.015).hex(),
      active: purple[700],
      border: purple[900],
      color: purple[50]
    }
  }
};

export default palette;
